import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Loader2, CreditCard, Zap, AlertCircle } from 'lucide-react';
import { PlanLimitsDashboard } from './PlanLimitsDashboard';
import { useSettingspageTranslations } from '@/hooks/useSettingspageTranslations';
import { useOrganization } from '@/hooks/useOrganization';
import { toast } from 'sonner';
import api from '@/services/api';
import { useNavigate } from 'react-router-dom';
import { PlanUpgradeModal } from './PlanUpgradeModal';

export function SubscriptionManagement() {
  const i18n = useSettingspageTranslations();
  const { organization, isLoading } = useOrganization();
  const [isOpeningPortal, setIsOpeningPortal] = useState(false);
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);
  const navigate = useNavigate();

  const handleUpgrade = () => {
    setShowUpgradeModal(true);
  };

  const handleUpgradeComplete = () => {
    setShowUpgradeModal(false);
    // Refresh the page or navigate to success page
    navigate('/payment-success');
  };

  const handleOpenBillingPortal = async () => {
    if (!organization?.stripe_customer_id) {
      toast.error(i18n.organization.subscription.messages.noActiveSubscriptionFound);
      return;
    }

    try {
      setIsOpeningPortal(true);
      const response = await api.post('/api/organizations/billing/portal-session/');

      if (response.data?.portal_url) {
        // Open billing portal in new tab
        window.open(response.data.portal_url, '_blank');
        toast.success(i18n.organization.subscription.messages.openingBillingPortal);
      } else {
        throw new Error('No portal URL received');
      }
    } catch (error: any) {
      let errorMessage = i18n.organization.subscription.messages.unableToAccessBillingPortal;

      if (error.response?.data?.error) {
        errorMessage = error.response.data.error;
      }

      toast.error(errorMessage);
    } finally {
      setIsOpeningPortal(false);
    }
  };

  const getPlanDisplayName = () => {
    if (!organization?.plan) return i18n.organization.subscription.plans.unknown;

    // Map plan names to translations
    const planTranslations: Record<string, string> = {
      basic: i18n.organization.subscription.plans.basic,
      business: i18n.organization.subscription.plans.business,
      enterprise: i18n.organization.subscription.plans.enterprise,
    };

    return planTranslations[organization.plan] || i18n.organization.subscription.plans.unknown;
  };

  const getPlanPrice = () => {
    if (!organization?.plan) return null;

    // Plan pricing based on billing cycle
    const pricing: Record<string, { monthly: number; annual: number }> = {
      basic: { monthly: 49, annual: 42 },
      business: { monthly: 739, annual: 628 },
      enterprise: { monthly: 1470, annual: 1250 },
    };

    const planPricing = pricing[organization.plan];
    if (!planPricing) return null;

    const price =
      organization.billing_cycle === 'annual' ? planPricing.annual : planPricing.monthly;
    const cycle =
      organization.billing_cycle === 'annual'
        ? i18n.organization.subscription.billing.annual.toLowerCase()
        : i18n.organization.subscription.billing.monthly.toLowerCase();

    return `€${price}/${cycle}`;
  };

  const hasActiveSubscription =
    organization?.stripe_customer_id && organization?.is_active_subscription;
  const isTrialing = organization?.is_trial && !organization?.is_trial_expired;
  const daysUntilTrialExpires = organization?.days_until_trial_expires || 0;

  if (isLoading) {
    return (
      <div className="flex items-center justify-center p-8">
        <Loader2 className="size-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <>
      <PlanUpgradeModal
        isOpen={showUpgradeModal}
        onClose={() => setShowUpgradeModal(false)}
        onSuccess={handleUpgradeComplete}
      />

      <div className="space-y-8">
        {/* Plan Usage Dashboard */}
        <div>
          <h3 className="mb-4 text-lg font-semibold">{i18n.organization.subscription.planUsage}</h3>
          <PlanLimitsDashboard />
        </div>

        {/* Trial Warning */}
        {isTrialing && daysUntilTrialExpires <= 7 && (
          <Alert className="border-orange-200 bg-orange-50">
            <AlertCircle className="size-4 text-orange-600" />
            <AlertDescription className="text-orange-800">
              {i18n.organization.subscription.messages.trialExpiresIn.replace(
                '{{days}}',
                daysUntilTrialExpires.toString()
              )}
              . {i18n.organization.subscription.messages.upgradeToKernLogic}.
            </AlertDescription>
          </Alert>
        )}

        {/* Subscription Management */}
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">
              {i18n.organization.subscription.cardTitles.subscriptionManagement}
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-6">
            {/* Current Plan Info */}
            <div className="flex items-center justify-between">
              <div>
                <p className="text-2xl font-semibold text-foreground">{getPlanDisplayName()}</p>
                {getPlanPrice() && (
                  <p className="text-sm text-muted-foreground">{getPlanPrice()}</p>
                )}
              </div>
            </div>

            {/* Action Buttons */}
            <div className="flex flex-col space-y-3 sm:flex-row sm:space-x-3 sm:space-y-0">
              <Button onClick={handleUpgrade} className="flex-1">
                <Zap className="mr-2 size-4" />
                {i18n.organization.subscription.upgradeNow}
              </Button>

              {hasActiveSubscription ? (
                <Button
                  variant="outline"
                  onClick={handleOpenBillingPortal}
                  disabled={isOpeningPortal}
                  className="flex-1"
                >
                  {isOpeningPortal ? (
                    <>
                      <Loader2 className="mr-2 size-4 animate-spin" />
                      {i18n.organization.subscription.actions.opening}
                    </>
                  ) : (
                    <>
                      <CreditCard className="mr-2 size-4" />
                      {i18n.organization.subscription.billing.manageBilling}
                    </>
                  )}
                </Button>
              ) : (
                <Button variant="outline" disabled className="flex-1">
                  <CreditCard className="mr-2 size-4" />
                  {i18n.organization.subscription.billing.noActiveBilling}
                </Button>
              )}
            </div>

            {/* Support Link */}
            <div className="border-t pt-4 text-center">
              <p className="text-sm text-muted-foreground">
                Need help?{' '}
                <a href="mailto:support@kernlogic.com" className="text-primary hover:underline">
                  {i18n.organization.subscription.contactSupport}
                </a>
              </p>
            </div>
          </CardContent>
        </Card>
      </div>
    </>
  );
}
